import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, MapPin, Download, Crosshair, ExternalLink, Mail, Phone, IdCard, CreditCard, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  name: '',
  cedula_rif: '',
  email: '',
  phone: '',
  address: '',
  location: { lat: 0, lng: 0 },
  notes: '',
  credit_limit_requested: '',
  contact1_name: '', contact1_phone: '+58', contact1_relation: '',
  contact2_name: '', contact2_phone: '+58', contact2_relation: '',
  birth_date: '', occupation: '', workplace: '', monthly_income: ''
};

const PHONE_RE = /^\+58\d{10}$/;

const CREDIT_BADGE = {
  none: { label: 'Sin crédito (solo contado)', cls: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Crédito pendiente de aprobación', cls: 'bg-orange-100 text-orange-700' },
  approved: { label: 'Crédito aprobado', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Crédito rechazado', cls: 'bg-red-100 text-red-700' }
};

export default function Clients() {
  const { user } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [approveAmounts, setApproveAmounts] = useState({});

  const loadClients = async () => {
    try {
      const response = await api.getClients({ company_id: getCurrentCompanyId() });
      setClients(response.data);
    } catch (error) {
      toast.error('SalesPRO: Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await api.getClients({ company_id: getCurrentCompanyId() });
        if (active) setClients(response.data);
      } catch (error) {
        toast.error('SalesPRO: Error al cargar clientes');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validaciones estrictas
    if (!formData.name.trim() || !formData.cedula_rif.trim() || !formData.email.trim()
        || !formData.phone.trim() || !formData.address.trim()) {
      toast.error('SalesPRO: Todos los campos son obligatorios');
      return;
    }
    const phones = [formData.phone, formData.contact1_phone, formData.contact2_phone].map((p) => (p || '').replace(/\s/g, ''));
    if (!phones.every((p) => PHONE_RE.test(p))) {
      toast.error('SalesPRO: El teléfono del cliente y los 2 teléfonos de contacto son obligatorios con formato +58XXXXXXXXXX');
      return;
    }
    if (!formData.contact1_name.trim() || !formData.contact2_name.trim()) {
      toast.error('SalesPRO: Indica el nombre de los 2 contactos de referencia');
      return;
    }
    if (!formData.location.lat || !formData.location.lng) {
      toast.error('SalesPRO: Debes capturar la ubicación del cliente (lat/lng) para que el despachador pueda llegar');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        company_id: getCurrentCompanyId() || undefined,
        credit_limit_requested: parseFloat(formData.credit_limit_requested) || 0
      };
      await api.createClient(payload);
      toast.success(`SalesPRO: Cliente afiliado. Acceso: ${formData.email} · contraseña = número de cédula (${formData.cedula_rif.replace(/\D/g, '')}).`, { duration: 8000 });
      setDialogOpen(false);
      loadClients();
      setFormData(EMPTY_FORM);
    } catch (error) {
      const msg = error.response?.data?.detail || 'SalesPRO: Error al crear cliente';
      // Si es 409 (exclusividad), mostrar como warning destacado
      if (error.response?.status === 409) {
        toast.warning(msg, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCredit = async (client) => {
    const raw = approveAmounts[client.client_id];
    const amount = raw !== undefined && raw !== '' ? parseFloat(raw) : (client.credit_limit_requested || 0);
    if (isNaN(amount) || amount <= 0) { toast.error('SalesPRO: Ingresa un monto de crédito válido'); return; }
    try {
      await api.approveClientCredit(client.client_id, amount);
      toast.success(`SalesPRO: Crédito aprobado por $${amount.toFixed(2)} para ${client.name}`);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al aprobar crédito');
    }
  };

  const handleRejectCredit = async (client) => {
    try {
      await api.rejectClientCredit(client.client_id);
      toast.success(`SalesPRO: Crédito rechazado para ${client.name}`);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al rechazar crédito');
    }
  };

  const handleCaptureLocation = () => {
    // Intento 1: Geolocalización HTML5 nativa (funciona sin Google Maps)
    if (!navigator.geolocation) {
      toast.error('SalesPRO: Tu navegador no soporta geolocalización. Ingresa lat/lng manualmente.');
      return;
    }
    toast.info('SalesPRO: Obteniendo ubicación...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData(f => ({ ...f, location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }));
        toast.success(`SalesPRO: Ubicación capturada (${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)})`);
      },
      (err) => {
        toast.error(`SalesPRO: No se pudo obtener ubicación (${err.message}). Ingresa manualmente.`);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleOpenInMaps = (client) => {
    const { lat, lng } = client.location || {};
    let url;
    if (lat && lng) {
      url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}`;
    }
    window.open(url, '_blank');
  };

  const handleExport = async () => {
    try {
      const response = await api.exportClientsReport();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clientes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SalesPRO: Reporte exportado');
    } catch (error) {
      toast.error('SalesPRO: Error al exportar');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="clients-loading">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Clientes</h1>
          <p className="text-sm text-[#64748B] mt-1">Cartera de clientes (exclusiva por empresa)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-clients-button">
            <Download size={16} className="mr-1.5" />
            Exportar Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setFormData(EMPTY_FORM); }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-client-button">
                <Plus size={16} className="mr-1.5" />
                Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
              <DialogHeader>
                <DialogTitle>Afiliar nuevo cliente</DialogTitle>
                <p className="text-xs text-[#64748B] mt-1">
                  Todos los campos son obligatorios. El cliente recibirá credenciales automáticas para acceder a SalesPRO.
                </p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nombre completo *</Label>
                  <Input
                    data-testid="client-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Juan Pérez"
                  />
                </div>
                <div>
                  <Label>Cédula / RIF *</Label>
                  <Input
                    data-testid="client-cedula-input"
                    value={formData.cedula_rif}
                    onChange={(e) => setFormData({ ...formData, cedula_rif: e.target.value })}
                    required
                    placeholder="V-12345678 o J-401234567"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    data-testid="client-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="cliente@ejemplo.com"
                  />
                </div>
                <div>
                  <Label>Teléfono *</Label>
                  <Input
                    data-testid="client-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+584121234567"
                  />
                </div>
                <div className="rounded-lg border border-[#E2E8F0] p-3 space-y-3" data-testid="client-contacts-section">
                  <p className="text-sm font-semibold text-[#022C22]">Contactos de referencia (obligatorios)</p>
                  {[1, 2].map((n) => (
                    <div key={n} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input placeholder={`Nombre contacto ${n} *`} value={formData[`contact${n}_name`]} onChange={(e) => setFormData({ ...formData, [`contact${n}_name`]: e.target.value })} data-testid={`client-contact${n}-name`} />
                      <Input placeholder="+58XXXXXXXXXX *" value={formData[`contact${n}_phone`]} onChange={(e) => setFormData({ ...formData, [`contact${n}_phone`]: e.target.value })} data-testid={`client-contact${n}-phone`} />
                      <Input placeholder="Parentesco / relación" value={formData[`contact${n}_relation`]} onChange={(e) => setFormData({ ...formData, [`contact${n}_relation`]: e.target.value })} data-testid={`client-contact${n}-relation`} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label>Fecha de nacimiento</Label><Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} data-testid="client-birth-date" /></div>
                  <div><Label>Ocupación</Label><Input value={formData.occupation} onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} data-testid="client-occupation" /></div>
                  <div><Label>Lugar de trabajo</Label><Input value={formData.workplace} onChange={(e) => setFormData({ ...formData, workplace: e.target.value })} data-testid="client-workplace" /></div>
                  <div><Label>Ingreso mensual aprox.</Label><Input value={formData.monthly_income} onChange={(e) => setFormData({ ...formData, monthly_income: e.target.value })} placeholder="$300" data-testid="client-income" /></div>
                </div>
                <div>
                  <Label>Dirección textual *</Label>
                  <Input
                    data-testid="client-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    placeholder="Calle 5, edificio Vista, piso 3, Caracas"
                  />
                </div>
                <div className="border border-[#E2E8F0] rounded-md p-3 bg-[#F8F9FA]">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Ubicación GPS *</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleCaptureLocation}
                      data-testid="capture-location-button"
                    >
                      <Crosshair size={14} className="mr-1" /> Capturar ubicación
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        type="number"
                        step="any"
                        data-testid="client-lat-input"
                        placeholder="Latitud"
                        value={formData.location.lat || ''}
                        onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lat: parseFloat(e.target.value) || 0 } })}
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        step="any"
                        data-testid="client-lng-input"
                        placeholder="Longitud"
                        value={formData.location.lng || ''}
                        onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lng: parseFloat(e.target.value) || 0 } })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-2">
                    💡 Próximamente: integración con Google Maps para seleccionar pin. Por ahora usa &quot;Capturar ubicación&quot; desde el dispositivo del vendedor.
                  </p>
                </div>
                <div>
                  <Label>Notas (opcional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Referencia adicional..."
                  />
                </div>
                <div className="border border-[#E2E8F0] rounded-md p-3 bg-[#F0F7FF]">
                  <Label className="flex items-center gap-1.5 text-sm">
                    <CreditCard size={14} /> Línea de crédito propuesta (opcional)
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    data-testid="client-credit-input"
                    value={formData.credit_limit_requested}
                    onChange={(e) => setFormData({ ...formData, credit_limit_requested: e.target.value })}
                    placeholder="Ej: 1000.00"
                    className="mt-1"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1.5">
                    Propón un monto de crédito. Quedará <b>pendiente de aprobación del Master</b>. Hasta que se apruebe, solo se permiten operaciones de contado.
                  </p>
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-client-button">
                  {submitting ? 'Afiliando...' : 'Afiliar cliente'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map((client) => (
          <Card key={client.client_id} className="border border-[#E2E8F0] shadow-sm" data-testid={`client-card-${client.client_id}`}>
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold">{client.name}</CardTitle>
              <p className="text-xs text-[#94A3B8] font-mono">{client.cedula_rif}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-[#64748B]">
                <div className="flex items-center gap-2"><IdCard size={14} /><span className="font-mono text-xs">{client.cedula_rif}</span></div>
                <div className="flex items-center gap-2"><Mail size={14} /><span className="truncate">{client.email}</span></div>
                <div className="flex items-center gap-2"><Phone size={14} />{client.phone}</div>
                <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5" /><span>{client.address}</span></div>
              </div>

              {/* Línea de crédito */}
              <div className="mt-3 pt-3 border-t border-[#E2E8F0]" data-testid={`client-credit-${client.client_id}`}>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${(CREDIT_BADGE[client.credit_status || 'none'] || CREDIT_BADGE.none).cls}`}>
                  <CreditCard size={12} /> {(CREDIT_BADGE[client.credit_status || 'none'] || CREDIT_BADGE.none).label}
                </span>
                {client.credit_status === 'approved' && (
                  <p className="text-xs text-green-700 mt-1.5 font-medium">Límite aprobado: ${(client.credit_limit_approved || 0).toFixed(2)}</p>
                )}
                {client.credit_status === 'pending' && (
                  <p className="text-xs text-orange-700 mt-1.5">Monto solicitado: ${(client.credit_limit_requested || 0).toFixed(2)}</p>
                )}

                {/* Master: aprobar / rechazar */}
                {isMaster && client.credit_status === 'pending' && (
                  <div className="mt-2 flex flex-col gap-2 bg-[#F8FAFC] rounded-md p-2 border border-[#E2E8F0]">
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      data-testid={`approve-amount-${client.client_id}`}
                      placeholder={`Monto a aprobar (def. ${(client.credit_limit_requested || 0).toFixed(2)})`}
                      value={approveAmounts[client.client_id] ?? ''}
                      onChange={(e) => setApproveAmounts({ ...approveAmounts, [client.client_id]: e.target.value })}
                      className="h-8 text-sm"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleApproveCredit(client)} data-testid={`approve-credit-${client.client_id}`}>
                        <Check size={14} className="mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRejectCredit(client)} data-testid={`reject-credit-${client.client_id}`}>
                        <X size={14} className="mr-1" /> Rechazar
                      </Button>
                    </div>
                  </div>
                )}
                {isMaster && client.credit_status === 'approved' && (
                  <Button size="sm" variant="outline" className="mt-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRejectCredit(client)} data-testid={`revoke-credit-${client.client_id}`}>
                    <X size={14} className="mr-1" /> Revocar crédito
                  </Button>
                )}
              </div>

              <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                {(client.location?.lat || client.address) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenInMaps(client)}
                    className="flex-1"
                    data-testid={`open-maps-${client.client_id}`}
                  >
                    <ExternalLink size={14} className="mr-1" /> Abrir en Maps
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {clients.length === 0 && (
        <div className="text-center py-12" data-testid="no-clients-message">
          <p className="text-gray-600">Aún no hay clientes registrados</p>
        </div>
      )}
    </div>
  );
}
